/**
 * Resolution of Acceptance
 * Phase 2 (CATCH) document with variants by asset type
 * Requires Secretary Treasurer signature + Board Member signatures
 * 
 * NOTE: Board member signature blocks are added DYNAMICALLY by the component,
 * not in this template. The placeholder <!-- BOARD_MEMBER_SIGNATURES --> is replaced
 * at generation time with actual member signatures based on UNA membership.
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generateAcceptanceResolutionHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page { 
      size: Letter; 
      margin: 0.75in; 
      @bottom-center { 
        content: "Page " counter(page) " of " counter(pages); 
        font-family: Cambria, Georgia, 'Times New Roman', serif; 
        font-size: 9pt; 
        color: #666;
      } 
    }
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0;
      color: #1a1a1a;
    }
    h1 {
      text-align: center;
      font-size: 18pt;
      margin: 0 0 20pt 0;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      border-bottom: 2px solid #c9a961;
      padding-bottom: 14pt;
    }
    .whereas, .resolved {
      margin-bottom: 14pt;
      text-align: justify;
    }
    .whereas::before {
      content: "WHEREAS, ";
      font-weight: bold;
    }
    .resolved::before {
      content: "RESOLVED, ";
      font-weight: bold;
    }
    .signature-section {
      margin-top: 40pt;
      page-break-inside: avoid;
    }
    .signature-block {
      margin-top: 24pt;
      margin-bottom: 20pt;
      padding: 20pt;
      border: 1px solid #ddd;
      border-radius: 4pt;
      background-color: #fafafa;
      page-break-inside: avoid;
    }
    .signature-line {
      border-bottom: 2px solid #333;
      width: 3.5in;
      min-height: 0.6in;
      margin-bottom: 8pt;
      background-color: white;
    }
    .signature-label {
      font-size: 10pt;
      margin-top: 6pt;
      font-weight: 600;
    }
    .date-line {
      border-bottom: 1px solid #666;
      width: 2in;
      display: inline-block;
      margin-left: 8pt;
    }
    .section-title {
      font-size: 13pt;
      margin: 24pt 0 14pt 0;
      border-bottom: 1px solid #bfbfbf;
      padding-bottom: 6pt;
      color: #262626;
      font-weight: bold;
    }
  </style>
</head>
<body>
  <h1>Resolution of Acceptance</h1>
  
  ${getWhereasClauses(workflowData)}
  
  ${getResolvedClauses(workflowData)}
  
  <div class="signature-section">
    <!-- BOARD_MEMBER_SIGNATURES -->
    
    <div style="page-break-before: avoid; margin-top: 40pt;">
      <h3 class="section-title">{{primary_officer_role}} Attestation</h3>
      <p style="margin-bottom: 20pt; font-style: italic; font-size: 10pt;">
        The {{primary_officer_role}} hereby attests that this resolution was duly adopted:
      </p>
      
      <div class="signature-block">
        <div class="signature-line" data-signer-id="{{treasury_secretary_id}}" data-signature-role="primary_officer"></div>
        <div class="signature-label">{{treasury_secretary_name}}, {{primary_officer_role}}</div>
        <div class="signature-label">On behalf of {{una_name}}</div>
        <div style="margin-top: 10pt;">Date: <span class="date-line"></span></div>
      </div>
    </div>
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}

function getWhereasClauses(workflowData: any): string {
  const assetType = workflowData.asset_type;
  const baseWhereas = `
  <div class="whereas">
    {{una_name}} is an unincorporated nonprofit association formed under the laws of {{una_state}} 
    and operating within ecclesiastical jurisdiction for the advancement of sacred purposes;
  </div>
  
  <div class="whereas">
    {{contributor_name}}, a member of {{una_name}}, has offered to make a private member 
    contribution to {{receiving_entity}} as evidenced by a Resolution Regarding Authorization 
    of Contribution dated {{contribution_date}};
  </div>
  `;
  
  let assetSpecific = '';
  
  if (assetType?.includes('Business Entity')) {
    assetSpecific = `
  <div class="whereas">
    The offered contribution consists of a member interest in {{entity_legal_name}}, 
    a {{entity_type}} formed under the laws of {{entity_state_of_formation}}, which interest 
    will be stewarded by {{receiving_entity}} for the benefit of the membership;
  </div>
  
  <div class="whereas">
    The acceptance of this business entity interest aligns with the purposes of {{una_name}} 
    and will enable {{receiving_entity}} to advance its sacred mission through proper 
    ecclesiastical stewardship;
  </div>
    `;
  } else if (assetType?.includes('Real Estate')) {
    assetSpecific = `
  <div class="whereas">
    The offered contribution consists of an interest in real property commonly known as 
    {{property_address}}, {{property_city}}, {{property_state}}, which property will be 
    stewarded by {{receiving_entity}} for purposes including: {{property_purpose}};
  </div>
  
  <div class="whereas">
    The acceptance of this real property aligns with the purposes of {{una_name}} and will 
    enable {{receiving_entity}} to provide facilities and resources for the benefit of the 
    membership;
  </div>
    `;
  } else if (assetType?.includes('Vehicle')) {
    assetSpecific = `
  <div class="whereas">
    The offered contribution consists of a vehicle described as {{vehicle_description}}, 
    VIN {{vehicle_vin}}, which vehicle will be stewarded by {{receiving_entity}} to provide 
    transportation for officers and members;
  </div>
  
  <div class="whereas">
    The acceptance of this vehicle aligns with the purposes of {{una_name}} and will enable 
    {{receiving_entity}} to support its operations and activities;
  </div>
    `;
  } else if (assetType === 'Cash/Bank Account') {
    assetSpecific = `
  <div class="whereas">
    The offered contribution consists of a cash contribution described as: {{contribution_description}}, 
    which funds will be stewarded by {{receiving_entity}} for the advancement of the UNA's purposes;
  </div>
  
  <div class="whereas">
    The acceptance of this cash contribution aligns with the purposes of {{una_name}} and will 
    provide financial resources to support its sacred mission;
  </div>
    `;
  } else {
    assetSpecific = `
  <div class="whereas">
    The offered contribution consists of an asset described as {{asset_type}}, which asset 
    will be stewarded by {{receiving_entity}} for the benefit of the membership;
  </div>
  
  <div class="whereas">
    The acceptance of this contribution aligns with the purposes of {{una_name}} and will 
    enable {{receiving_entity}} to advance its sacred mission;
  </div>
    `;
  }
  
  return baseWhereas + assetSpecific;
}

function getResolvedClauses(workflowData: any): string {
  const assetType = workflowData.asset_type;
  
  let assetSpecific = '';
  
  if (assetType?.includes('Business Entity')) {
    assetSpecific = `
  <div class="resolved">
    {{receiving_entity}} hereby formally accepts stewardship of the member interest in 
    {{entity_legal_name}} offered by {{contributor_name}}, and commits to stewarding this 
    business entity in accordance with the purposes of {{una_name}};
  </div>
  
  <div class="resolved">
    The {{primary_officer_role}} is authorized to execute an Operating Agreement and all other 
    instruments necessary to formalize {{receiving_entity}}'s acceptance and establish proper 
    management structures for the contributed entity;
  </div>
    `;
  } else if (assetType?.includes('Real Estate')) {
    assetSpecific = `
  <div class="resolved">
    {{receiving_entity}} hereby formally accepts stewardship of the real property interest 
    located at {{property_address}}, {{property_city}}, {{property_state}}, offered by 
    {{contributor_name}}, and commits to stewarding this property in accordance with the 
    purposes of {{una_name}};
  </div>
  
  <div class="resolved">
    The {{primary_officer_role}} is authorized to execute all instruments necessary to formalize 
    {{receiving_entity}}'s acceptance and establish proper stewardship of the property;
  </div>
    `;
  } else if (assetType?.includes('Vehicle')) {
    assetSpecific = `
  <div class="resolved">
    {{receiving_entity}} hereby formally accepts stewardship of the vehicle described as 
    {{vehicle_description}}, VIN {{vehicle_vin}}, offered by {{contributor_name}}, and commits 
    to stewarding this vehicle in accordance with the purposes of {{una_name}};
  </div>
  
  <div class="resolved">
    The {{primary_officer_role}} is authorized to execute all instruments necessary to formalize 
    the title transfer and establish proper stewardship of the vehicle;
  </div>
    `;
  } else {
    assetSpecific = `
  <div class="resolved">
    {{receiving_entity}} hereby formally accepts stewardship of the contribution offered by 
    {{contributor_name}}, and commits to stewarding this asset in accordance with the purposes 
    of {{una_name}};
  </div>
  
  <div class="resolved">
    The {{primary_officer_role}} is authorized to execute all instruments necessary to formalize 
    {{receiving_entity}}'s acceptance and establish proper stewardship;
  </div>
    `;
  }
  
  return `
  <div class="resolved">
    This acceptance is made within the ecclesiastical jurisdiction of {{una_name}}, and the 
    contributed asset shall be held and stewarded according to the sacred purposes and 
    governing documents of the UNA;
  </div>
  
  ${assetSpecific}
  
  <div class="resolved">
    This resolution shall be preserved in the ecclesiastical records of {{una_name}}, and 
    a Contribution Filing Certificate shall be prepared to document this acceptance for 
    governance purposes.
  </div>
  `;
}
